import "../../../../assets/styles/Common.css";
import { useState, useRef } from "react";
import { useEffect } from "react";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Eduvatelogo from "../../../../assets/images/Eduvatelogo.svg";
import InviteAFriendModal from "../../modals/InviteAFriendModal";
import ShareYourFeedbackModal from "../../modals/ShareYourFeedbackModal";
import useCanvasDetection from "../../../hooks/useCanvasDetection";
import GoPremiumModal from "../../modals/GoPremiumModal";
import GoForUnlimitedQuestionsModal from "../../modals/GoForUnlimitedQuestionsModal";
import { fetchUsersAllQuestions } from "../../../api/fetchUsersAllQuestions";
import LoadingOverlay from "../../overlays/LoadingOverlay";
import { logout } from "../../../api/logout";
import QuillionzProUpgradeModal from "../../../components/modals/QuillionzProUpgradeModal";
import { getDecrypted, getDecryptedBatch, setEncrypted } from "../../../utils/cryptoUtils";

const AppNavigation = (props) => {

  const navigate  = useNavigate();
  const location  = useLocation();
  const isCanvas  = useCanvasDetection();
  const dropdownRef = useRef(null);

  // ── UI-only state (no sensitive data) ──────────────────────────────────────
  const [showMenu, setShowMenu]                     = useState(false);
  const [logoutDialog, setLogoutDialog]             = useState(false);
  const [showInviteModal, setShowInviteModal]       = useState(false);
  const [showFeedbackModal, setShowFeedbackModal]   = useState(false);
  const [showGoPremiumModal, setShowGoPremiumModal] = useState(false);
  const [showUnlimitedModal, setShowUnlimitedModal] = useState(false);
  const [showDropdown, setShowDropdown]             = useState(false);
  const [isLoading, setIsLoading]                   = useState(false);
  const [showManageSubscription, setShowManageSubscription] = useState(false);

  // ── Encrypted localStorage values — all live in state ─────────────────────
  const [username, setUsername]                 = useState("");
  const [Email, setEmail]                       = useState("");
  const [userGPTKey, setUserGPTKey]             = useState("LIMITED");
  const [Version, setVersion]                   = useState("");
  const [remainingAttempt, setRemainingAttempt] = useState(0);
  const [isPlanExpired, setIsPlanExpired]       = useState("false");

  // ── Step 1: Decrypt all needed values once on mount ────────────────────────
  useEffect(() => {
    const loadSecureData = async () => {
      const {
        username:      decryptedUsername,
        email:         decryptedEmail,
        userGPTKey:    decryptedGPTKey,
        Version:       decryptedVersion,
        UserAttempts:  decryptedAttempts,
        isPlanExpired: decryptedExpired,
      } = await getDecryptedBatch([
        "username",
        "email",
        "userGPTKey",
        "Version",
        "UserAttempts",
        "isPlanExpired",
      ]);

      setUsername(decryptedUsername ?? "");
      setEmail(decryptedEmail ?? "");
      setUserGPTKey(decryptedGPTKey ?? "LIMITED");
      setVersion(decryptedVersion ?? "");
      setRemainingAttempt(decryptedAttempts ?? 0);
      // isPlanExpired is stored as boolean by setEncryptedBatch — normalise to string
      // so existing comparisons like  isPlanExpired === "true"  keep working.
      setIsPlanExpired(String(decryptedExpired ?? "false"));
    };

    loadSecureData();
  }, []);

  // ── Close dropdown on outside click ────────────────────────────────────────
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, []);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const toggleMenu     = () => setShowMenu((p) => !p);
  const toggleDropdown = () => setShowDropdown((p) => !p);

  const handleInviteFriendClick = () => setShowInviteModal(true);
  const handleFeebackClick      = () => setShowFeedbackModal(true);
  const handleGoPremiumClick    = () => setShowGoPremiumModal(true);
  const handleModalClose        = () => setLogoutDialog(false);
  const handleAccountInfo       = () => navigate("/userinfo");
  const handleRemainingAttempts = () => setShowUnlimitedModal(true);
  const ContactforAPI           = () => { window.location.href = "mailto:support@quillionz.com"; };

  const handleAttemptClick = () => {
    if (remainingAttempt > 0) {
      setRemainingAttempt((prev) => prev - 1);
    }
  };

  const handleNewContent = () => {
    // These are non-sensitive session keys — plain removal is fine
    [
      "title", "content", "doc_id", "domain", "keywordsResponse",
      "contentReadiness", "reviewContentResponse", "selectedKeywordsArray",
      "totalQuestionsGenerated", "whQuestionsCount", "isSampleButtonClicked",
      "QAreqbody",
    ].forEach((k) => localStorage.removeItem(k));

    if (location.pathname === "/submitcontent") {
      if (!isCanvas) {
        window.location.href = "/submitcontent";
      } else {
        props.setContent("");
        props.setTitle("");
        props.setDomain("");
        props.setSelectedFile(null);
        navigate("/submitcontent");
      }
    } else {
      navigate("/submitcontent");
    }
  };

  const handleUsersQuestionsHistory = async () => {
    if (!isCanvas) {
      navigate("/history");
    } else {
      setIsLoading(true);
      // Email is now in state — no need to read localStorage here
      const userQuestionsHistory = await fetchUsersAllQuestions(Email);
      localStorage.setItem(
        "usersQuestionHistory",
        JSON.stringify(userQuestionsHistory.data.doc_list)
      );
      navigate("/history");
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      setIsLoading(true);
      // Email is in state — no raw localStorage.getItem needed
      await logout(Email);
      localStorage.clear();
      sessionStorage.removeItem("isLoggedIn");
      setIsLoading(false);
      navigate("/");
      setLogoutDialog(true);
    } catch (error) {
      console.error("Error while logout:", error);
      setLogoutDialog(true);
    }
  };

  // Background color for Attempts Circle icon
  const circleIconStyle = (remaining) => {
    const percentage = ((25 - remaining) / 25) * 100;
    return {
      background:
        remaining <= 2
          ? `linear-gradient(to top, red ${percentage}%, transparent ${percentage}%, transparent 100%)`
          : "",
      backgroundSize: "200% 200%",
      animation: "wave 0.1s linear infinite",
      border: remaining <= 2 ? "2px solid red" : "2px solid #000000",
    };
  };

  return (
    <div>
      <div className="">
        {/* Navigatin bar starts here */}

        <nav className="bg-[#042739] h-[12vh] p-[10px] flex justify-between items-center md:flex sm:grid">
          <div className="flex items-center w-[20vh] pl-[2.5rem]">
            {/* Eduvate Logo */}
            <Link to={"/submitcontent"}>
              <img src={Eduvatelogo} alt="Logo"/>
            </Link>
          </div>

          {/* Hamburger menu button (visible only on small devices) */}
          <div className="md:hidden">
            <button onClick={toggleMenu}>
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16m-7 6h7"
                />
              </svg>
            </button>
          </div>

          {/* Menu items (visible on larger devices) */}
          <div
            className={`md:flex items-center ${showMenu ? "block" : "hidden"}`}
          >
            <ul className="md:flex space-x-4 ">
              {/* Invite a Friend */}
              {/* {isCanvas === true ? (
                ""
              ) : (
                <li className="nav-item ">
                  <button
                    className="nav-button "
                    onClick={handleInviteFriendClick}
                  >
                    <i className="fa-solid fa-user-group "></i> Invite a Friend
                  </button>
                </li>
              )} */}

              {/* New Content */}
              <li className="nav-item">
                <button className="nav-button" onClick={handleNewContent}>
                  <i className="fa-solid fa-file-circle-plus"></i> New Content
                </button>
              </li>

              {/* History */}
              <li className="nav-item">
                <button
                  className="nav-button"
                  onClick={() => {
                    handleUsersQuestionsHistory();
                  }}
                >
                  <i className="fa-solid fa-clock-rotate-left"></i> History
                </button>
              </li>

              {/* Remaining Question Sets */}
              {userGPTKey === "LIMITED" ? (
                <li className="nav-item bg-[#0c5882] rounded-lg hover:opacity-90 ">
                  {remainingAttempt > 0 && isPlanExpired == "false" ? (
                    <Tooltip
                      title={`Quiz Sets Left : ${remainingAttempt} of 5`}
                      arrow
                      placement="top"
                      TransitionComponent={Zoom}
                      TransitionProps={{ timeout: 300 }}
                    >
                      <button
                        className=""
                        id="go_premium_button"
                        onClick={handleRemainingAttempts}
                      >
                        <div className="flex flex-row items-center">
                          <div>Quiz Sets Left</div>
                          <div
                            className="circle-icon ml-2 bg-[#000000]"
                            style={circleIconStyle(remainingAttempt)}
                          >
                           <span className={`${remainingAttempt <= 2 ? "count text-white" : "count text-orange-500"}`}>{remainingAttempt}</span>
                          </div>
                        </div>
                      </button>
                    </Tooltip>
                  ) : isPlanExpired === "true" ? 
                    <Tooltip
                      title={
                        <span>
                          <i
                            className="pe-2 fa-solid fa-triangle-exclamation"
                            style={{ color: "white" }}
                          ></i>
                          Want to create more? Upgrade your plan!
                        </span>
                      }
                      arrow
                      placement="top"
                      TransitionComponent={Zoom}
                      TransitionProps={{ timeout: 300 }}
                    >
                      <button
                        className=""
                        id="go_premium_button"
                        // onClick={() => setShowManageSubscription(true)}
                         onClick={() => Version === 'Free' ? setShowManageSubscription(true) : setShowUnlimitedModal(true)}
                      >
                        Upgrade to Plan
                      </button>
                    </Tooltip>
                   : Version === 'Free' ? 
                    <>
                    <Tooltip
                      title={
                        <span>
                          <i
                            className="pe-2 fa-solid fa-triangle-exclamation"
                            style={{ color: "white" }}
                          ></i>
                          Purchase your plan!
                        </span>
                      }
                      arrow
                      placement="top"
                      TransitionComponent={Zoom}
                      TransitionProps={{ timeout: 300 }}
                    >
                      <button
                        className=""
                        id="go_premium_button"
                        // onClick={() => setShowManageSubscription(true)}
                         onClick={() => Version === 'Free' ? setShowManageSubscription(true) : setShowUnlimitedModal(true)}
                      >
                        Purchase Plan
                      </button>
                    </Tooltip>
                  </>
                  :
                    <>
                    <Tooltip
                      title={
                        <span>
                          <i
                            className="pe-2 fa-solid fa-triangle-exclamation"
                            style={{ color: "white" }}
                          ></i>
                          Want to create more? Upgrade your plan!
                        </span>
                      }
                      arrow
                      placement="top"
                      TransitionComponent={Zoom}
                      TransitionProps={{ timeout: 300 }}
                    >
                      <button
                        className=""
                        id="go_premium_button"
                        // onClick={() => setShowManageSubscription(true)}
                         onClick={() => Version === 'Free' ? setShowManageSubscription(true) : setShowUnlimitedModal(true)}
                      >
                        Upgrade to Plan
                      </button>
                    </Tooltip>
                    </>
                  }
                </li>
              ) :
                    <Tooltip
                      title={
                        <span>
                          <i
                            className="pe-2 fa-solid fa-triangle-exclamation"
                            style={{ color: "white" }}
                          ></i>
                          Want to create more? Upgrade your plan!
                        </span>
                      }
                      arrow
                      placement="top"
                      TransitionComponent={Zoom}
                      TransitionProps={{ timeout: 300 }}
                    >
                      <button
                        className=""
                        id="go_premium_button"
                        // onClick={() => setShowManageSubscription(true)}
                        onClick={() => Version === 'Free' ? setShowManageSubscription(true) : setShowUnlimitedModal(true)}
                      >
                        Upgrade to Plan
                      </button>
                    </Tooltip>
                  }

              {/* Dropdown menu for "Username" starts here */}
              {isCanvas ? (
                ""
              ) : (
                <li className="nav-item relative text-start">
                  <div className="relative" ref={dropdownRef}>
                    <button className="nav-button" onClick={toggleDropdown}>
                      <i className="me-1 fa-solid fa-user text-white"></i>
                      <span className="text-white">{username || Email }</span> <i className="fa-solid fa-caret-down text-white"></i>
                      {/* Dropdown will appear when you click on "Username" */}
                      {showDropdown && (
                        <div className="absolute dropdown top-[2rem] right-0 w-[100px] bg-white text-black rounded-md shadow-lg ]">
                          <ul className="py-2">
                            {/* Account Info */}
                            <li
                              className="px-4 py-2 hover:text-[#0c5882]  hover:bg-gray-100 hover:rounded-3xl cursor-pointer"
                              onClick={handleAccountInfo}
                            >
                              Account Info
                            </li>

                            {/* Logout */}
                            <li
                              className="px-4 py-2 hover:text-[#0c5882] hover:bg-gray-100 hover:rounded-3xl cursor-pointer"
                              onClick={handleLogout}
                            >
                              Logout
                            </li>
                          </ul>
                        </div>
                      )}
                      {/* Dropdown menu for "Username" ends here */}
                    </button>
                  </div>
                </li>
              )}
            </ul>
          </div>
        </nav>

        {/* Navigation bar ends here */}

        {/* Render the Invite A Friend Modal component */}
        {isCanvas === true ? (
          ""
        ) : (
          <InviteAFriendModal
            open={showInviteModal}
            onClose={() => setShowInviteModal(false)}
          />
        )}

        {/* Render the Share Your Feedback Modal component */}
        {isCanvas ? (
          ""
        ) : (
          <ShareYourFeedbackModal
            open={showFeedbackModal}
            onClose={() => setShowFeedbackModal(false)}
          />
        )}

        {/* Render the Go Premium Modal component */}
        {/* "Go For Unlimited" is currently on hold. We will resume work on it later */}
        <GoPremiumModal
          open={showGoPremiumModal}
          onClose={() => setShowGoPremiumModal(false)}
        />

        <GoForUnlimitedQuestionsModal
          open={showUnlimitedModal}
          onClose={() => setShowUnlimitedModal(false)}
        />

{/* Render the Go Premium Modal component */}
        <QuillionzProUpgradeModal
          isOpen={showManageSubscription}
          onClose={() => setShowManageSubscription(false)}
        />
        {/* Loading overlay */}
        {isLoading && <LoadingOverlay />}
      </div>
    </div>
  );
};

export default AppNavigation;
